import type { User as BetterAuthUser } from "better-auth/types";
import { atom, getDefaultStore, useSetAtom } from "jotai";

// Do not import authClient at module scope: this module ships in the critical
// bundle (via context/index), so better-auth must stay behind the dynamic
// imports inside the async helpers below.

/**
 * Extended User type (matching Better Auth user structure)
 */
export interface User extends BetterAuthUser {
  phoneNumber?: string;
  phoneNumberVerified?: boolean;
  birthday?: string;
  gender?: "male" | "female";
}

/**
 * Session data structure (matching Better Auth session structure)
 */
export interface SessionData {
  user: User;
  session: {
    id: string;
    token: string;
    expiresAt: Date;
    createdAt: Date;
    updatedAt: Date;
  };
}

interface SessionState {
  data: SessionData | null;
  isPending: boolean;
}

// Main session state atom
export const sessionStateAtom = atom<SessionState>({
  data: null,
  isPending: true,
});

// Derived atoms for convenience
export const sessionAtom = atom((get) => get(sessionStateAtom).data);
export const sessionUserAtom = atom(
  (get) => get(sessionStateAtom).data?.user ?? null,
);
export const sessionTokenAtom = atom(
  (get) => get(sessionStateAtom).data?.session.token ?? null,
);
export const isSignedInAtom = atom((get) => !!get(sessionStateAtom).data?.user);
export const isSessionPendingAtom = atom(
  (get) => get(sessionStateAtom).isPending,
);

/**
 * Session actions hook for syncing and clearing session
 *
 * Usage:
 * - After sign in/sign up/update user info -> call `syncSessionFromAuthClient()`
 * - After sign out/reset password -> call `clearSession()` (it handles authClient.signOut internally)
 */
export function useSessionActions() {
  const setSessionState = useSetAtom(sessionStateAtom);

  /**
   * Sync session from Better Auth client to Atom
   * Call this after successful sign in/sign up/update user info
   */
  const syncSessionFromAuthClient = async () => {
    try {
      const { authClient } = await import("@/lib/auth-client");
      const { data: session } = await authClient.getSession();

      if (session) {
        setSessionState({
          data: session as SessionData,
          isPending: false,
        });
      } else {
        setSessionState({ data: null, isPending: false });
      }
    } catch {
      // ignore errors
    }
  };

  /**
   * Clear session from Atom
   * Call this after sign out or on 401 error
   * Better Auth handles cookie/storage cleanup via signOut
   */
  const clearSession = async () => {
    try {
      const { authClient } = await import("@/lib/auth-client");
      await authClient.signOut();
    } catch {
      // Local state must clear even if the chunk load or signOut request fails.
    }
    setSessionState({ data: null, isPending: false });
  };

  return { syncSessionFromAuthClient, clearSession };
}

/**
 * Get session token from store (for use in API interceptors)
 * Returns cached token without making API call
 */
export function getSessionTokenFromStore(): string | null {
  const store = getDefaultStore();
  return store.get(sessionTokenAtom);
}

/**
 * Clear session from store (for use in 401 interceptors)
 */
export function clearSessionFromStore() {
  const store = getDefaultStore();
  store.set(sessionStateAtom, { data: null, isPending: false });
}

/**
 * Sync session from Better Auth client to store (for use outside React components)
 * Call this after successful sign in/sign up/update user info
 */
export async function syncSessionFromAuthClientInStore() {
  const store = getDefaultStore();
  try {
    const { authClient } = await import("@/lib/auth-client");
    const { data: session } = await authClient.getSession();

    if (session) {
      store.set(sessionStateAtom, {
        data: session as SessionData,
        isPending: false,
      });
    } else {
      store.set(sessionStateAtom, { data: null, isPending: false });
    }
  } catch {
    // ignore errors
  }
}
